/**
 * Loader for pm-trader-wasm - browser-side BLS signing for TraderAuth.
 *
 * Replaces the old pm-trader-tool `serve` HTTP calls in trader-crypto.js.
 * Modeled on @dusk/connect's driver.ts (loadWasmDataDriver): raw
 * WebAssembly.instantiate, manual alloc/dealloc + pointer/length
 * marshalling, no wasm-bindgen. See pm-trader-wasm/README.md for the ABI.
 *
 * The one twist vs. a pure data-driver: generate_keypair() needs entropy,
 * which this module deliberately does not pull from wasm-bindgen/js-sys
 * (see pm-trader-wasm/Cargo.toml). Instead the wasm module imports a single
 * `env.js_random_fill(ptr, len)` function, which we supply here via
 * crypto.getRandomValues.
 */

const WASM_URL = "/pm-trader-signer.wasm";

const OUT_START = 4 * 1024;
const OUT_MAX = 64 * 1024;
const OUT_TRIES = 5;

let driverPromise = null;

function loadDriver() {
  if (!driverPromise) {
    driverPromise = instantiate();
  }
  return driverPromise;
}

async function instantiate() {
  const res = await fetch(WASM_URL);
  if (!res.ok) {
    throw new Error(`failed to fetch ${WASM_URL} (${res.status} ${res.statusText})`);
  }
  const bytes = await res.arrayBuffer();

  const importObject = {
    env: {
      js_random_fill(ptr, len) {
        const view = new Uint8Array(exports.memory.buffer, ptr, len);
        crypto.getRandomValues(view);
      },
    },
  };

  const { instance } = await WebAssembly.instantiate(bytes, importObject);
  const exports = instance.exports;

  if (
    !exports?.memory ||
    typeof exports.alloc_buf !== "function" ||
    typeof exports.dealloc_buf !== "function" ||
    typeof exports.generate_keypair !== "function" ||
    typeof exports.keypair_from_seed !== "function" ||
    typeof exports.sign !== "function"
  ) {
    throw new Error("invalid pm-trader-signer.wasm: missing required exports");
  }

  return exports;
}

function withAllocBytes(exports, bytes, fn) {
  const ptr = exports.alloc_buf(bytes.length);
  try {
    new Uint8Array(exports.memory.buffer, ptr, bytes.length).set(bytes);
    return fn(ptr, bytes.length);
  } finally {
    exports.dealloc_buf(ptr, bytes.length);
  }
}

function withAllocStr(exports, s, fn) {
  return withAllocBytes(exports, new TextEncoder().encode(s), fn);
}

/**
 * Calls `ffi(outPtr, outCap)`, which per the pm-trader-wasm ABI returns
 * bytes-written (>=0) or -1 if outCap was too small. Retries with a bigger
 * buffer on -1. Returns the parsed JSON, and throws if the response has an
 * "error" field.
 */
function callJson(exports, ffi) {
  let outCap = OUT_START;
  for (let attempt = 0; attempt < OUT_TRIES; attempt++) {
    const outPtr = exports.alloc_buf(outCap);
    try {
      const written = ffi(outPtr, outCap);
      if (written === -1) {
        if (outCap >= OUT_MAX) {
          throw new Error("pm-trader-wasm: output buffer too small (max retries reached)");
        }
        outCap = Math.min(outCap * 2, OUT_MAX);
        continue;
      }
      const bytes = new Uint8Array(exports.memory.buffer, outPtr, written).slice();
      const json = JSON.parse(new TextDecoder().decode(bytes));
      if (json.error) throw new Error(`pm-trader-wasm: ${json.error}`);
      return json;
    } finally {
      exports.dealloc_buf(outPtr, outCap);
    }
  }
  throw new Error("pm-trader-wasm: output buffer too small (max retries reached)");
}

/** Generates a new BLS trading keypair entirely in-page. Never leaves the browser. */
export async function generateKeypairWasm() {
  const exports = await loadDriver();
  return callJson(exports, (outPtr, outCap) => exports.generate_keypair(outPtr, outCap));
}

/**
 * Derives a BLS trading keypair from a 32-byte seed (hex), entirely in-page.
 * Deterministic: the same seed always yields the same keypair - used to
 * derive the trading key from `keccak(domain || wallet signature)` so it
 * never needs local storage or manual regeneration (see trader-crypto.js
 * `deriveTradingKeyFromWallet`).
 */
export async function deriveKeypairFromSeedWasm(seedHex) {
  const exports = await loadDriver();
  const reqJson = JSON.stringify({ seed_hex: seedHex });
  return withAllocStr(exports, reqJson, (inPtr, inLen) =>
    callJson(exports, (outPtr, outCap) => exports.keypair_from_seed(inPtr, inLen, outPtr, outCap))
  );
}

/** Signs an already-hashed 32-byte message (hex) with a BLS secret key (hex). */
export async function signWasm(skHex, msgHex, insecure = false) {
  const exports = await loadDriver();
  const reqJson = JSON.stringify({ sk_hex: skHex, msg_hex: msgHex, insecure });
  return withAllocStr(exports, reqJson, (inPtr, inLen) =>
    callJson(exports, (outPtr, outCap) => exports.sign(inPtr, inLen, outPtr, outCap))
  );
}
